<template>
  <div id="main-content">
    <ApiLoading v-if="apiStatus.loading || apiStatus.fail" :apiError="apiStatus.fail" />
    <template v-else>
      <div class="content-page-banner">
        <div class="banner-content">
          <h1>{{ referenceData.selectedProductHeading.informationText }}</h1>
          <div class="underline"></div>
          <div v-html="referenceData.selectedProductInformationText.informationText" />
        </div>
      </div>
      <div class="page-content">
        <h2 class="h4">The cover you have selected is...</h2>
        <article class="selected-product-card fr-view">
          <section class="product-info">
            <h2>{{ selectedProducts.recommendationName }}</h2>
            <PriceFactorStatement
              v-if="changeCoverEnteredData !== null"
              :data="changeCoverEnteredData"
              :isPlainText="false"
              :isChangeCover="true"
              class="price-factor-statement-container"
            >
              <template v-slot:title>
                <h3 class="h6">{{ referenceData.selectedProductSubheading.informationText }}</h3>
              </template>
              <template v-slot:link>
                <button
                  class="non-button-style"
                  @click.prevent="
                    redirectToChangeCoverStep('MyDetails', isFromEmailQuote ? false : true);
                    resetVuex();
                  "
                  aria-label="Change my details"
                >
                  {{ referenceData.selectedProductChangeDetailsLinkLabel.informationText }}
                </button>
              </template>
            </PriceFactorStatement>
            <h3>Product inclusions</h3>
            <ul v-if="selectedProducts.products.length > 0">
              <li v-for="product in selectedProducts.products" :key="product.productCode">{{ product.productUsp }}</li>
            </ul>
            <QuoteFeatures v-if="quoteData !== null" :quote="quoteData" :informationText="referenceData.selectedProductGuideInformationText.informationText" />
            <a v-if="!isFromEmailQuote" @click.prevent="redirectToChangeCoverStep('MyRecommendations')" aria-label="Back to my recommendations">
              {{ referenceData.selectedProductMyRecommendationsLinkLabel.informationText }}
            </a>
          </section>
          <section class="quote-info-amount">
            <h3>Your premium</h3>
            <p>
              <span>{{ convertToCurrencyString(selectedProducts.price) }}</span>
              /&nbsp;{{ paymentFrequency }}
            </p>
            <!-- prettier-ignore -->
            <p v-if="!isExtrasOnly"><strong>${{ selectedExcess }} Hospital excess</strong>, payable once per adult per financial year.</p>
            <p>
              Cover for a
              <strong>{{ summaryCoverComposition(selectedProducts.composition) }}</strong>
            </p>
            <p>
              Effective from
              <strong>{{ format(new Date(), 'dd MMMM yyyy') }}</strong>
            </p>
            <Alert v-if="closedProduct" :message="referenceData.selectedProductClosedProductInformationText.informationText" />
            <div class="form-page-nav">
              <section>
                <button class="button secondary" @click.prevent="sendQuoteEmail()">Email me this quote</button>
                <button class="button crimson" @click.prevent="redirectToCaptureInfoPage">{{ referenceData.selectedProductConfirmButtonLabel.informationText }}</button>
              </section>
            </div>
          </section>
        </article>
        <EmailQuoteModal
          :modalActive="isEmailModalVisible"
          :apiStatus="apiStatus.quoteEmail"
          :emailSentTo="emailSentTo"
          @emailQuoteHook="emailQuoteHook"
          @closeModal="isEmailModalVisible = false"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { cloneDeep, isEmpty } from 'lodash';
import { ProductType, StateCodes, CoverComposition, NeedsAnalysisEligibility } from '@general/js/enums';
import QuoteFeatures from '@general/js/components/quoteAcceptance/quoteFeatures.vue';
import MemberDetailsServices from '@/services/MemberDetails';
import { ChangeCover as ChangeCoverServices } from '@/services/MyCover';
import { convertToCurrencyString, firstLetterToUpperCase, getCurrentDate, summaryCoverComposition, reverseFormatFormDate, formatDate } from '@general/js/utils';
import PriceFactorStatement from '@general/js/components/PriceFactorStatement.vue';
import { ApiLoading, Alert } from '@/components';
import Services from '@/services/MyCover/ChangeCover/ChangeProduct';
import { convertCodeToValue, findPolicyHolder, getPartnerDetailsMP } from '@/utils';
import { HospitalSituation, VeteranCardTypeOptions, OperationType } from '@/enums';
import { formatQuoteLHCABD, formatEmailQuoteLHCABD, formatEaiLhcToObj } from '@/utils/ChangeProduct/GetPremiumPrice/PersonUtils';
import { findDependents } from '@/utils/ChangeProduct/ExtraUtils';
import { mapEligibilityNames, convertPregnancyFlagStringToBool } from '@/utils/ChangeCoverUtils';
import { ERROR_404_PAGE_NOT_FOUND } from '@/router/routeNames';
import EmailQuoteModal from './EmailQuoteModal.vue';
import ChangeCoverMixins from './ChangeCoverMixins';
import CaptureInformationMixins from './CaptureInformationMixins';

export default {
  name: 'NewProductSelection',

  components: { PriceFactorStatement, QuoteFeatures, ApiLoading, Alert, EmailQuoteModal },

  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  data() {
    return {
      isEmailModalVisible: false,
      newEmail: null,
      emailSentTo: null,
      apiStatus: {
        loading: false,
        fail: false,
        quoteEmail: {
          loading: false,
          fail: false,
          success: false,
          isInvalidEmail: false,
        },
        productHistory: {
          loading: false,
          fail: false,
        },
        membershipDetails: {
          loading: false,
          fail: false,
        },
      },
    };
  },

  created() {
    const currentMembership = this.memberships?.find((membership) => {
      return membership.memberNumber === this.$route.params.policyNumber;
    });

    // When the user is returning from email quote, check if the quote is for their membership, otherwise redirect to 404
    if (!currentMembership) {
      this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
    }

    if (this.$route.query.quoteId) {
      this.updateIsFromEmailQuote(true);
      this.fetchChangeProductRefData(this.apiStatus, false);
      this.fetchQuoteDetails(this.$route.query.quoteId);
    }
  },

  methods: {
    convertToCurrencyString,
    format,
    summaryCoverComposition,

    updateCurrentAndInitialDetails() {
      // This updates the "current" and "initial" data in vuex (i.e. currentEligibility, currentState, policyHolderInitialLhc, etc.)
      // These current/initial details are the details of the membership prior to policy changes as we use them as a reference point in CAI steps to determine if policy changes have occurred

      // Get Eligibility details
      if (this.eligibilityDetails) {
        const currentEligibility = mapEligibilityNames(this.eligibilityDetails.eligibilityTyp);
        if (currentEligibility) {
          this.updateCurrentEligibility(currentEligibility);
        }
      }

      // Get State details
      if (this.memberDetails) {
        const policyHolder = findPolicyHolder(this.memberDetails, false, true);
        if (policyHolder) {
          const stateCode = policyHolder.residentialAddress.state ?? policyHolder.address.state;
          this.updateCurrentState(stateCode);
        }
      }

      // Get active dependents and add them to vuex for Step 2. read-only Partner Information & No of children
      if (this.memberDetails) {
        const policyHolder = findPolicyHolder(this.memberDetails, true, true);
        if (policyHolder) {
          if (policyHolder.lhc) {
            this.updateInitialPolicyHolderLhc(formatEaiLhcToObj(policyHolder.lhc));
          }
          this.updateInitialPolicyHolderAbd(policyHolder.abdDetails);
          this.updateInitialPolicyHolderPortalAbd(policyHolder.portalAgeBasedDiscount);
          // Store in CAI initial vuex for computing difference on submission
          this.updateInitialPolicyHolderVeteranCard(this.formatVeteranCardDetails(policyHolder));
        }

        const partner = getPartnerDetailsMP(this.memberDetails, true);
        if (partner) {
          if (partner.lhc) {
            this.updateInitialPartnerLhc(formatEaiLhcToObj(partner.lhc));
          }
          this.updateInitialPartnerAbd(partner.abdDetails);
          this.updateInitialPartnerPortalAbd(partner.portalAgeBasedDiscount);
          this.updateInitialPartnerVeteranCard(this.formatVeteranCardDetails(partner));
        }

        // Get number of children
        const children = findDependents(this.memberDetails);
        const numberOfChildren = children.length > 0 ? children.length.toString() : null;
        this.updateCurrentNumberOfChildren(numberOfChildren);
      }
    },

    formatVeteranCardDetails(memberObj) {
      try {
        const veteranDetails = memberObj?.activeVeteranCard;

        if (veteranDetails) {
          const veteranCardType =
            veteranDetails.cardType === VeteranCardTypeOptions.VETERAN_GOLD_CODE
              ? VeteranCardTypeOptions.VETERAN_GOLD_CARD_PROPER
              : VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER;

          const veteranObj = {
            op: OperationType.NONE,
            haveVeteranCard: true,
            veteranCardType,
            id: veteranDetails.id,
            startDate: veteranDetails.startDate,
            veteranCardConditionOptions: [],
          };

          const acceptedConditions = memberObj.veteranCardAcceptedConditions;

          if (veteranCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER && acceptedConditions && acceptedConditions.length > 0) {
            veteranObj.veteranCardConditionOptions = acceptedConditions.map((condition) => {
              return {
                id: condition.id,
                startDate: condition.startDate,
                op: OperationType.NONE,
                returnValue: condition.code,
              };
            });
          }
          return veteranObj;
        }
        return { haveVeteranCard: false, veteranCardType: null, veteranCardConditionOptions: [] };
      } catch (error) {
        logger.error('Error getting veteran card details: ', error);
        return null;
      }
    },

    async fetchMembershipDetails() {
      this.apiStatus.membershipDetails.loading = true;
      this.apiStatus.membershipDetails.fail = false;

      try {
        // Temp workaround to check if ph/partner is contributor on landing page
        const resMembershipDetails = await (this.coverDetails ? Promise.resolve({ ...this.coverDetails }) : Services.getMembershipDetails(this.memberNumber));
        // Get members in policy if members do not yet exist
        if (!this.members) {
          const allMembers = await MemberDetailsServices.getMembers(this.memberNumber);
          this.updateMembers(allMembers);
        }
        this.updateMemberDetails(resMembershipDetails.members);
        this.updateEligibilityDetails(resMembershipDetails.eligibility);
        this.getPolicyHolderInformation();

        this.updateMembershipProducts(resMembershipDetails.products);
        // checks if current policy is closed hospital and extras product
        this.getClosedHospitalProduct();
        this.getClosedExtrasProduct();

        this.updateMembershipPolicy(resMembershipDetails.membership);
        this.getInitialRebateTierLevel();

        // update fetched values into vuex store
        this.updateValuesIntoVuex();
      } catch (error) {
        this.apiStatus.membershipDetails.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.membershipDetails.loading = false;
      }
    },

    async fetchProductHistory() {
      this.apiStatus.productHistory.loading = true;
      this.apiStatus.productHistory.fail = false;

      try {
        if (this.memberDetails) {
          const policyHolder = findPolicyHolder(this.memberDetails, false, true);
          if (policyHolder) {
            const response = await ChangeCoverServices.fetchMemberProductHistory(this.memberNumber, policyHolder.personCode);
            this.updatePolicyHolderProductHistory(response.policies);
          }

          const partner = getPartnerDetailsMP(this.memberDetails, true);

          if (partner) {
            const response = await ChangeCoverServices.fetchMemberProductHistory(this.memberNumber, partner.personCode);
            this.updatePartnerProductHistory(response.policies);
          }
        }
      } catch (error) {
        this.apiStatus.productHistory.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.productHistory.loading = false;
      }
    },

    reverseFormatFormDate,

    resetVuex() {
      if (this.isFromEmailQuote) {
        this.resetChangeCoverData();
        this.resetData();
      }
    },

    processRebateTierValue(rebateTier) {
      if (rebateTier.toLowerCase() === 'none' || rebateTier === '') {
        return null;
      }
      return rebateTier;
    },

    async fetchQuoteDetails() {
      this.apiStatus.loading = true;
      try {
        // fetch current cover details when returning from email quote
        await this.fetchMembershipDetailsAPI(this.apiStatus, null, false);
        await this.fetchProductHistory();
        this.updateCurrentAndInitialDetails();

        const payload = {
          quoteId: this.$route.query.quoteId,
          existingProducts: this.getExistingProducts(),
        };

        const quoteDetails = await Services.getRecommendedProducts(payload, true); // true is only for mock service
        this.updateStepDataIntoVuex(quoteDetails);
        const quotePrice = await this.fetchQuotePrice(quoteDetails);
        const selectedProducts = {
          coverType: quoteDetails.hospitalAndExtras,
          composition: quoteDetails.composition,
          ...quoteDetails.recommendations[0],
          ...quotePrice,
        };

        this.updateMyRecommendations({ recommendations: quoteDetails.recommendations, existingProducts: quoteDetails.existingProducts });
        this.updateSelectedProducts(selectedProducts);
        this.updatePaymentFrequency(firstLetterToUpperCase(quoteDetails.recommendations[0].frequency));
        this.updateSelectedExcess(quoteDetails.recommendations[0].excess);
      } catch (error) {
        this.apiStatus.fail = true;
        logger.log(error);
      } finally {
        this.apiStatus.loading = false;
      }
    },

    async fetchQuotePrice(quoteDetails) {
      const recommendation = quoteDetails.recommendations[0];
      try {
        const payload = {
          quoteType: 'C',
          contributionMethod: 'DD',
          updateCRMQuote: false,
          memberNumber: this.memberNumber,
          composition: quoteDetails.composition,
          frequency: recommendation.frequency,
          eligibility: { eligibilityType: quoteDetails.eligibility.eligibilityType },
          numberOfChildren: quoteDetails.numberOfChildren ? Number(quoteDetails.numberOfChildren) : 0,
          productList: this.getProductList([recommendation], [recommendation.excess]),
          startDate: getCurrentDate(),
          paymentStartDate: getCurrentDate(),
          state: convertCodeToValue(StateCodes, Number(quoteDetails.state)),
          rebate: this.processRebateTierValue(quoteDetails.rebate.rebateTier ?? this.membershipPolicy.rebateTier)
            ? {
                rebateTier: this.processRebateTierValue(quoteDetails.rebate.rebateTier ?? this.membershipPolicy.rebateTier),
              }
            : null,
          quoteId: this.$route.query.quoteId,
        };

        if (quoteDetails.mainPolicyHolder) {
          const existingPolicyHolderDetails = findPolicyHolder(this.memberDetails, false, true);
          const existingLhcOverrulePolicyHolder = existingPolicyHolderDetails.lhcOverrule !== null;

          const policyHolderLHCABDQuoteDetails = formatEmailQuoteLHCABD(quoteDetails.mainPolicyHolder, this.policyHolderState, existingLhcOverrulePolicyHolder);
          const policyHolderLHCABD =
            policyHolderLHCABDQuoteDetails &&
            policyHolderLHCABDQuoteDetails?.lhc === null &&
            policyHolderLHCABDQuoteDetails?.abd === null &&
            this.policyHolderState !== HospitalSituation.RETAINING
              ? formatQuoteLHCABD(existingPolicyHolderDetails, this.policyHolderState, existingLhcOverrulePolicyHolder)
              : policyHolderLHCABDQuoteDetails;

          payload.mainPolicyHolder = {
            firstName: quoteDetails.mainPolicyHolder?.firstName,
            lastName: quoteDetails.mainPolicyHolder?.lastName,
            middleName: quoteDetails.mainPolicyHolder?.middleName,
            email: quoteDetails.mainPolicyHolder?.email,
            dateOfBirth: quoteDetails.mainPolicyHolder?.dateOfBirth?.split('T')[0],
            covered: quoteDetails.composition !== CoverComposition.CHILDREN_ONLY,
            ageBasedDiscount: !isEmpty(policyHolderLHCABD?.abd) ? { ...policyHolderLHCABD?.abd } : null,
            lifetimeHealthCoverOverrule: !isEmpty(policyHolderLHCABD?.lhc) ? { ...policyHolderLHCABD?.lhc } : null,
            veteranCard: {
              hasVeteranCard: quoteDetails.mainPolicyHolder?.veteranCard?.hasVeteranCard,
              acceptedConditions: quoteDetails.mainPolicyHolder?.veteranCard?.acceptedConditions ?? [],
              cardType: quoteDetails.mainPolicyHolder?.veteranCard?.cardType,
            },
          };
        }

        if (quoteDetails.partner?.dateOfBirth) {
          const existingPartnerDetails = getPartnerDetailsMP(this.memberDetails, true);
          const quoteDetailsPartnerDOB = quoteDetails.partner?.dateOfBirth?.split('T')[0] ?? null;

          // Check if there is an existing partner with an existing lhcOverrule
          const existingLhcOverruleExistingPartner = quoteDetailsPartnerDOB === existingPartnerDetails?.dob && existingPartnerDetails?.lhcOverrule !== null;
          const partnerLHCABDQuoteDetails = formatEmailQuoteLHCABD(quoteDetails.partner, this.partnerHospitalState, existingLhcOverruleExistingPartner);

          const partnerLHCABD =
            partnerLHCABDQuoteDetails &&
            partnerLHCABDQuoteDetails?.lhc === null &&
            partnerLHCABDQuoteDetails?.abd === null &&
            this.partnerHospitalState !== HospitalSituation.RETAINING
              ? formatQuoteLHCABD(existingPartnerDetails, this.partnerHospitalState, existingLhcOverruleExistingPartner)
              : partnerLHCABDQuoteDetails;

          payload.partner = {
            covered: quoteDetails.composition !== CoverComposition.CHILDREN_ONLY,
            dateOfBirth: quoteDetails.partner?.dateOfBirth?.split('T')[0],
            ageBasedDiscount: !isEmpty(partnerLHCABD?.abd) ? { ...partnerLHCABD?.abd } : null,
            lifetimeHealthCoverOverrule: !isEmpty(partnerLHCABD?.lhc) ? { ...partnerLHCABD?.lhc } : null,
            veteranCard: {
              hasVeteranCard: quoteDetails.partner?.veteranCard?.hasVeteranCard,
              acceptedConditions: quoteDetails.partner?.veteranCard?.acceptedConditions ?? [],
              cardType: quoteDetails.partner?.veteranCard?.cardType,
            },
          };
        }

        // Send discharge date for ex-serving members if selected "recently discharged" during the quote flow
        if (quoteDetails.eligibility.eligibilityType === NeedsAnalysisEligibility.EX_SERVING && quoteDetails.eligibility.dischargeDate) {
          payload.eligibility.dischargeDate = formatDate(quoteDetails.eligibility.dischargeDate);
        }

        const quotePrice = await Services.getPrice(payload);
        const prices = quotePrice.productPrices[0];
        return {
          price: prices.calculationResultSetTotalResult,
          discountsAndLoadings: prices.discounts,
        };
      } catch (error) {
        this.apiStatus.fail = true;
        logger.log(error);
      }
      return null;
    },

    updateStepDataIntoVuex(data) {
      const step1 = {
        startDate: getCurrentDate(),
        state: convertCodeToValue(StateCodes, Number(data.state)),
        eligibility: data.eligibility.eligibilityType,
        dvaCard: false,
        name: `${data.mainPolicyHolder.firstName} ${data.mainPolicyHolder.lastName}`,
        DOB: data.mainPolicyHolder?.dateOfBirth?.split('T')[0],
        gender: null,
      };
      const step2 = {
        coverType: data.hospitalAndExtras,
        composition: data.composition,
        myCircumstances: this.membershipPolicy.myCircumstances,
        incomeTier: this.processRebateTierValue(data.rebate?.rebateTier ?? this.membershipPolicy.rebateTier) ?? 'none',
        rebateTier: this.processRebateTierValue(data.rebate?.rebateTier ?? this.membershipPolicy.rebateTier) ?? 'none',
        numberOfChildren: data.numberOfChildren ? Number(data.numberOfChildren) : 0,
        familyPlus: data.composition === CoverComposition.FAMILY_PLUS || data.composition === CoverComposition.SINGLE_PARENT_PLUS,
        policyHolder: {
          abd: data.mainPolicyHolder?.ageBasedDiscount,
          dob: data.mainPolicyHolder?.dateOfBirth?.split('T')[0],
          lhc: {
            cae: data.mainPolicyHolder?.lifetimeHealthCoverOverrule?.cae,
            date: data.mainPolicyHolder?.lifetimeHealthCoverOverrule?.date,
            lhcLoadingRate: data.mainPolicyHolder?.lifetimeHealthCoverOverrule?.lhcLoadingRate,
            overruledReason: data.mainPolicyHolder?.lifetimeHealthCoverOverrule?.overruledReason,
            startDate: data.mainPolicyHolder?.lifetimeHealthCoverOverrule?.startDate,
          },
          portalAgeBasedDiscount: {
            cda: data.mainPolicyHolder?.portalAgeBasedDiscount?.cda,
            currentlyReceived: data.mainPolicyHolder?.portalAgeBasedDiscount?.currentlyReceived,
            firstTimePHI: data.mainPolicyHolder?.portalAgeBasedDiscount?.firstTimePHI,
          },
          veteranCard: {
            haveVeteranCard: data.mainPolicyHolder?.veteranCard?.hasVeteranCard,
            veteranCardConditionOptions: data.mainPolicyHolder?.veteranCard?.acceptedConditions ?? [],
            veteranCardType: data.mainPolicyHolder?.veteranCard?.cardType,
          },
          eligibleForLHC: !!data.mainPolicyHolder?.lifetimeHealthCoverOverrule?.cae,
          eligibleForABD: !!data.mainPolicyHolder?.ageBasedDiscount?.cda,
        },
      };

      // TO DO: See if EAI can send us 'KeepAdult' flag in the response of get-product-recommendation because DOB comparison wont work if both DOB of new and old  are the same.
      // If Existing Partner or New Partner is added, partner dateOfBirth should be received from get-product-recommendations response
      if (data.partner?.dateOfBirth) {
        const partner = getPartnerDetailsMP(this.memberDetails, true);
        // Compare member details partner DOB with get-recommended-products response partner DOB to determine if new partner added
        const quoteDetailsPartnerDOB = data.partner?.dateOfBirth?.split('T')[0] ?? null;
        if (quoteDetailsPartnerDOB === partner?.dob) {
          // Existing partner - use cover details instead of get-recommended-products response
          step2.partner = {
            keepAdult: true,
            abd: partner?.abdDetails,
            dob: partner?.dob ? reverseFormatFormDate(partner?.dob) : '',
            firstName: partner?.firstName ? partner?.firstName : '',
            lastName: partner?.lastName ? partner?.lastName : '',
            lhc: {
              cae: partner?.lhc?.certifiedAgeOfEntry,
              date: partner?.lhc?.lhcEndDate,
              lhcLoadingRate: partner?.lhc?.lhcLoadingRate,
              overruledReason: partner?.lhc?.code,
              startDate: partner?.lhc?.lhcStartDate,
            },
            portalAgeBasedDiscount: {
              cda: partner?.portalAgeBasedDiscount?.cda,
              currentlyReceived: partner?.portalAgeBasedDiscount?.currentlyReceived,
              firstTimePHI: partner?.portalAgeBasedDiscount?.firstTimePHI,
            },
            veteranCard: {
              haveVeteranCard: partner?.hasVeteranCard,
              veteranCardConditionOptions: partner?.veteranCardAcceptedConditions ?? [],
            },
            eligibleForLHC: !!partner?.lhc?.certifiedAgeOfEntry,
            eligibleForABD: !!partner?.abdDetails?.cda,
          };
          step2.partnerDOB = reverseFormatFormDate(partner?.dob);
        } else {
          // New partner - use get-recommended-products response
          step2.partner = {
            keepAdult: false,
            abd: data.partner?.ageBasedDiscount,
            dob: reverseFormatFormDate(quoteDetailsPartnerDOB) ?? '',
            firstName: data.partner?.firstName ? data.partner?.firstName : '',
            lastName: data.partner?.lastName ? data.partner?.lastName : '',
            lhc: {
              cae: data.partner?.lifetimeHealthCoverOverrule?.cae,
              date: data.partner?.lifetimeHealthCoverOverrule?.date,
              lhcLoadingRate: data.partner?.lifetimeHealthCoverOverrule?.lhcLoadingRate,
              overruledReason: data.partner?.lifetimeHealthCoverOverrule?.overruledReason,
              startDate: data.partner?.lifetimeHealthCoverOverrule?.startDate,
            },
            portalAgeBasedDiscount: {
              cda: data.partner?.portalAgeBasedDiscount?.cda,
              currentlyReceived: data.partner?.portalAgeBasedDiscount?.currentlyReceived,
              firstTimePHI: data.partner?.portalAgeBasedDiscount?.firstTimePHI,
            },
            veteranCard: {
              haveVeteranCard: data.partner?.veteranCard?.hasVeteranCard,
              veteranCardConditionOptions: data.partner?.veteranCard?.acceptedConditions ?? [],
              veteranCardType: data.partner?.veteranCard?.cardType,
            },
            eligibleForLHC: !!data.partner?.lifetimeHealthCoverOverrule?.cae,
            eligibleForABD: !!data.partner?.ageBasedDiscount?.cda,
            newPartner: {
              DOB: reverseFormatFormDate(quoteDetailsPartnerDOB) ?? '',
              abd: data.partner?.ageBasedDiscount,
              lhc: {
                cae: data.partner?.lifetimeHealthCoverOverrule?.cae,
                date: data.partner?.lifetimeHealthCoverOverrule?.date,
                lhcLoadingRate: data.partner?.lifetimeHealthCoverOverrule?.lhcLoadingRate,
                overruledReason: data.partner?.lifetimeHealthCoverOverrule?.overruledReason,
                startDate: data.partner?.lifetimeHealthCoverOverrule?.startDate,
              },
              veteranCard: {
                haveVeteranCard: data.partner?.veteranCard?.hasVeteranCard,
                veteranCardConditionOptions: data.partner?.veteranCard?.acceptedConditions ?? [],
                veteranCardType: data.partner?.veteranCard?.cardType,
              },
              eligibleForLHC: !!data.partner?.lifetimeHealthCoverOverrule?.cae,
              eligibleForABD: !!data.partner?.ageBasedDiscount?.cda,
            },
          };
          step2.partnerDOB = reverseFormatFormDate(quoteDetailsPartnerDOB);
        }
      } else {
        step2.partner = null;
      }

      const step3 = {
        coverReason: data.coverReason,
        specificTreatmentCategory: data.medicalNeedClinical
          ? data.medicalNeedClinical.split(';').map((need) => {
              return { display: need, returnValue: need };
            })
          : [],
        specificTreatmentModality: data.medicalNeedExtras
          ? data.medicalNeedExtras.split(';').map((need) => {
              return { display: need, returnValue: need };
            })
          : [],
        noPregnancy: convertPregnancyFlagStringToBool(data.excludePregnancy),
      };

      this.updateMyDetails(step1);
      this.updateMyCover(step2);
      this.updateMyNeeds(step3);
    },

    sendQuoteEmail() {
      this.isEmailModalVisible = true;

      if (!this.apiStatus.quoteEmail.isInvalidEmail) {
        this.emailQuoteHook();
      }
    },
  },

  computed: {
    isExtrasOnly() {
      return !this.selectedProducts.products.some((product) => {
        return product.productType.toLowerCase() !== ProductType.EXTRAS_ONLY;
      });
    },

    closedProduct() {
      return this.closedHospitalProduct !== null || this.closedExtrasProduct !== null;
    },

    quoteData() {
      let flexiFeatures = [];
      let flexiLimit = null;

      this.selectedProducts.products.forEach((product) => {
        const foundItem = product.productProperties.find((feature) => {
          return feature.propertyName === 'Flexi-limit';
        });
        if (foundItem !== undefined) {
          flexiLimit = foundItem;
        }
      });

      const products = this.selectedProducts.products.map((product) => {
        // Get Flexi Features for package or extra product
        if (product.productType.toLowerCase() !== ProductType.HOSPITAL_ONLY) {
          flexiFeatures = product.productProperties
            .filter((feature) => {
              return feature.flexiLimit === true;
            })
            .map((feature) => {
              return {
                description: feature.qualifyingStatements,
                name: feature.propertyName,
                value: feature.propertyValue,
                waitingPeriod: feature.waitingPeriod,
                flexiLimit: feature.flexiLimit,
              };
            });
        }

        const productFeatures = product.productProperties
          .filter((feature) => {
            return feature.flexiLimit === false && feature.propertyName !== 'Flexi-limit';
          })
          .map((feature) => {
            return {
              description: feature.qualifyingStatements,
              featureType: feature.propertyType,
              name: feature.propertyName,
              value: feature.propertyValue,
              waitingPeriod: feature.waitingPeriod,
              flexiLimit: feature.flexiLimit,
            };
          });

        return {
          code: product.productCode,
          description: product.productDescription,
          excessList: cloneDeep(product.productExcess),
          features: productFeatures,
          guideUrl: product.productGuideLink,
          name: product.productName,
          type: product.productType.toLowerCase(),
        };
      });

      return {
        products,
        flexi: {
          description: flexiLimit?.qualifyingStatements,
          flexiLimit: flexiLimit?.propertyValue,
          waitingPeriod: flexiLimit?.waitingPeriod,
          flexiFeatures,
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.page-content {
  padding: 3rem 0;

  .selected-product-card {
    @include grid-layout;
    @include grid-template(1fr 1fr);

    padding: 2rem 1rem;
    justify-content: space-between;
    margin: 1rem 0;
    column-gap: 1.5rem;
    background-color: $WHITE;
    border-radius: 16px;
    box-shadow: 0 2px 6px 0 rgba(22,22,29,.06),0 2px 4px 0 rgba(22,22,29,.02);

    @include mobile {
      @include flex-col;
    }
  }

  .product-info {
    padding: 0 1.5rem;
    @include flex-col;

    h2 {
      margin-bottom: 3rem;
    }
  }

  .quote-info-amount {
    padding: 0 1.5rem;
    @include tablet-and-above {
      padding-left: 2rem;
    }

    p:first-of-type {
      font-weight: $BOLD;
      color: $PRIMARY_GREEN_100;

      span {
        font-size: font-size(3);
      }
    }
  }
}

.price-factor-statement-container {
  @include flex-col;
  margin-bottom: rem(56);

  h3 {
    margin-bottom: 1.5rem;
  }

  @include tablet-and-above {
    max-width: 880px;
  }
}

h3 {
  margin-bottom: 1.5rem;
}

:deep(#info-text) {
  margin-bottom: 3rem;
}

:deep(#premium-info) {
  margin-bottom: 1.5rem;
}

:deep(#price-factor-links) {
  @include flex-col;
  gap: 1.5rem;
}

:deep(.quote-features) {
  margin-bottom: unset;
}
</style>
